import React from 'react';
import PropTypes from 'prop-types';

import Autocomplete from './Autocomplete';

import {
  getZipcodeAutocompleteUrlByCountry,
  parseSuggestions,
  getZipcodeDisplay,
  parseSuggestionItem,
  showItemErrorClass,
} from '../../utilities';

import './ZipcodeAutocomplete.less';

export function ZipcodeAutocomplete({
  countryValue, disabled, inputError, inputValue, locationAutocompleteUrl,
  locationError, name, onChange, onSelectedItemChange, onSuggestionsError, touched,
}) {
  const zipcodeAutocompleteError = touched && (
    !!inputError || !!locationError
  );
  const zipcodeDisplay = getZipcodeDisplay(countryValue);

  return (
    <div className="ZipcodeAutocomplete">
      {/* this will announce any zipcode error on submit only */}
      <span id={`${name}_error`} aria-live="polite" className="screenreader_error">
        {touched && (inputError || locationError || '')}
      </span>
      <Autocomplete
        autocompleteUrl={getZipcodeAutocompleteUrlByCountry(locationAutocompleteUrl, countryValue)}
        error={inputError}
        name={name}
        inputProps={{
          'aria-describedby': zipcodeAutocompleteError ? `${name}_error` : '',
          'aria-invalid': zipcodeAutocompleteError,
          // chrome autofill and other autofill stuff like extensions messes up the zipcode autocomplete logic
          // autofill/autocomplete values don't emit a change event
          // so unless we hack something together, it's difficult to run on change behavior on those events
          // https://github.com/facebook/react/issues/1159
          // documentation on why autocomplete="off" doesn't prevent chrome autofill
          // https://developers.google.com/web/updates/2015/06/checkout-faster-with-autofill
          // thread about disabling google autocomplete
          // https://stackoverflow.com/a/15917221
          // note, you may think 'chrome-off' is dumb
          // until QA and myself mention that the chrome autofill doesn't seem to pop up on dev or stg, only on prod
          autoComplete: 'chrome-off',
          disabled,
          name,
          onChange,
          type: 'text',
          value: inputValue,
        }}
        // they must select a location suggestion for us to submit the location object from geoac
        itemError={locationError}
        itemHint="Please select an option from below:"
        itemToString={parseSuggestionItem}
        label={zipcodeDisplay}
        inputValue={inputValue}
        onSelectedItemChange={onSelectedItemChange}
        onSuggestionsError={onSuggestionsError}
        parse={parseSuggestions}
        showItemErrorClassByInput={showItemErrorClass(countryValue)}
        touched={touched}
      />
    </div>
  );
}

ZipcodeAutocomplete.propTypes = {
  countryValue: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  inputError: PropTypes.string,
  inputValue: PropTypes.string,
  locationAutocompleteUrl: PropTypes.string.isRequired,
  locationError: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSelectedItemChange: PropTypes.func.isRequired,
  onSuggestionsError: PropTypes.func,
  touched: PropTypes.bool,
};

ZipcodeAutocomplete.defaultProps = {
  disabled: false,
  inputError: null,
  inputValue: '',
  locationError: null,
  onSuggestionsError: () => {},
  touched: false,
};

export default ZipcodeAutocomplete;
